import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { Toaster } from './components/ui/sonner';
import CookieConsent from './components/CookieConsent';
import LogoutConfirmDialog from './components/LogoutConfirmDialog';

// Public pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPage from './pages/PrivacyPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import CGVPage from './pages/CGVPage';
import GradesPage from './pages/GradesPage';
import ContactPage from './pages/ContactPage';

// Client pages
import CatalogPage from './pages/CatalogPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import AddressesPage from './pages/AddressesPage';
import SAVPage from './pages/SAVPage';
import WalletPage from './pages/WalletPage';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminClients from './pages/admin/AdminClients';
import AdminEmployees from './pages/admin/AdminEmployees';
import AdminOrders from './pages/admin/AdminOrders';
import AdminProducts from './pages/admin/AdminProducts';
import AdminReservations from './pages/admin/AdminReservations';
import AdminPromos from './pages/admin/AdminPromos';
import AdminPricing from './pages/admin/AdminPricing';
import AdminShipping from './pages/admin/AdminShipping';
import AdminOrderPreparation from './pages/admin/AdminOrderPreparation';
import AdminSAVPage from './pages/admin/AdminSAVPage';

// Communication / Email Campaigns
import CampaignsDashboard from './pages/admin/communication/CampaignsDashboard';
import CreateCampaign from './pages/admin/communication/CreateCampaign';
import CampaignDetails from './pages/admin/communication/CampaignDetails';

import './App.css';

// Protected route wrapper
const ProtectedRoute = ({ children, requireApproved = true, requireAdmin = false }) => {
    const { isAuthenticated, isApproved, isAdmin, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (requireAdmin && !isAdmin) {
        return <Navigate to="/catalog" replace />;
    }

    if (requireApproved && !isApproved && !isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
                <div className="bg-white rounded-xl border border-amber-200 shadow-lg p-8 max-w-lg text-center">
                    <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3">Demande envoyée !</h2>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                        <p className="text-emerald-800 font-medium">
                            Votre demande de compte a bien été reçue.
                        </p>
                    </div>
                    <p className="text-slate-600 mb-6">
                        Elle sera traitée dans un délai de <strong>2 heures maximum</strong> durant nos heures d'ouverture.
                    </p>
                    <div className="bg-slate-50 rounded-lg p-4 mb-6">
                        <p className="text-sm text-slate-500 mb-2">Horaires de traitement</p>
                        <p className="font-medium text-slate-700">Lundi - Vendredi : 9h00 - 18h00</p>
                        <p className="text-sm text-slate-500 mt-2">Vous recevrez un email de confirmation dès validation.</p>
                    </div>
                    <button 
                        onClick={() => {
                            localStorage.removeItem('token');
                            window.location.href = '/';
                        }}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Retour à l'accueil
                    </button>
                </div>
            </div>
        );
    }

    return children;
};

// Public route (redirect if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, isAdmin, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to={isAdmin ? '/admin' : '/catalog'} replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<PrivacyPage />} />
            <Route path="/mentions" element={<MentionsLegalesPage />} />
            <Route path="/cgv" element={<CGVPage />} />
            <Route path="/grades" element={<GradesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Client routes */}
            <Route path="/catalog" element={<ProtectedRoute><CatalogPage /></ProtectedRoute>} />
            <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
            <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
            <Route path="/orders/:orderId" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
            <Route path="/order-success" element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} />
            <Route path="/addresses" element={<ProtectedRoute><AddressesPage /></ProtectedRoute>} />
            <Route path="/sav" element={<ProtectedRoute><SAVPage /></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />

            {/* Admin routes */}
            <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminClients /></ProtectedRoute>} />
            <Route path="/admin/clients" element={<ProtectedRoute requireAdmin><AdminClients /></ProtectedRoute>} />
            <Route path="/admin/employees" element={<ProtectedRoute requireAdmin><AdminEmployees /></ProtectedRoute>} />
            <Route path="/admin/orders" element={<ProtectedRoute requireAdmin><AdminOrders /></ProtectedRoute>} />
            <Route path="/admin/products" element={<ProtectedRoute requireAdmin><AdminProducts /></ProtectedRoute>} />
            <Route path="/admin/reservations" element={<ProtectedRoute requireAdmin><AdminReservations /></ProtectedRoute>} />
            <Route path="/admin/promos" element={<ProtectedRoute requireAdmin><AdminPromos /></ProtectedRoute>} />
            <Route path="/admin/pricing" element={<ProtectedRoute requireAdmin><AdminPricing /></ProtectedRoute>} />
            <Route path="/admin/shipping" element={<ProtectedRoute requireAdmin><AdminShipping /></ProtectedRoute>} />
            <Route path="/admin/preparation" element={<ProtectedRoute requireAdmin><AdminOrderPreparation /></ProtectedRoute>} />
            <Route path="/admin/sav" element={<ProtectedRoute requireAdmin><AdminSAVPage /></ProtectedRoute>} />
            
            {/* Communication / Email Campaigns */}
            <Route path="/admin/communication" element={<ProtectedRoute requireAdmin><CampaignsDashboard /></ProtectedRoute>} />
            <Route path="/admin/communication/new" element={<ProtectedRoute requireAdmin><CreateCampaign /></ProtectedRoute>} />
            <Route path="/admin/communication/campaign/:campaignId" element={<ProtectedRoute requireAdmin><CampaignDetails /></ProtectedRoute>} />
            <Route path="/admin/communication/history" element={<ProtectedRoute requireAdmin><CampaignsDashboard /></ProtectedRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <CartProvider>
                    <AppRoutes />
                    <Toaster position="top-center" richColors />
                    <CookieConsent />
                    <LogoutConfirmDialog />
                </CartProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
